import { type ReactNode } from 'react';
import { Check, ChevronDown, History, Lock, Plus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import {
  getSignatureCompletion,
  type SignatureCompletion,
  type WalaSignature,
} from './ics204SignatureSlots';
import Ics204SignatureProgressRing from './Ics204SignatureProgressRing';

type CreateMenuItem = {
  key: string;
  label: string;
  onSelect: () => void;
  disabled?: boolean;
  /** Spotlight id for a product tour. The Create button carries the first one. */
  tour?: string;
};

type Completion = SignatureCompletion;

type Ics204VersionStatusBarProps = {
  signatures: WalaSignature[];
  viewingPast: boolean;
  isCreatingSignedVersion: boolean;
  historyCount: number;
  signedCount: number;
  createItems: CreateMenuItem[];
  updatedAt?: string | null;
  updatedByUserName?: string | null;
  onOpenHistory: () => void;
  onOpenSigned: () => void;
  onViewLatest: () => void;
  onCancelCreateSigned: () => void;
  onSignThisVersion?: () => void;
  onDraftNewVersion?: () => void;
  draftNewVersionDisabled?: boolean;
  /** Override completion (e.g. ICS-215 Prepared By only). */
  getCompletion?: (signatures: WalaSignature[]) => Completion;
  signedMenuLabel?: string;
  hideProgressRing?: boolean;
  lockedMessage?: string;
  /** Tasks do not enforce signed-version edit locks; hide the lock copy. */
  hideLockedMessage?: boolean;
  /** Tooltip title for the progress ring (ICS-204 default: Signature progress). */
  progressTooltipTitle?: string;
  /** Colored banner only. Reports place it inside the card header. */
  embedded?: boolean;
  /** Layers have no signed chain, so the signed-versions item stays hidden. */
  hideSignedMenu?: boolean;
  /** Right side of the banner, after the version actions. */
  trailing?: ReactNode;
  /** Optional node after the progress ring (e.g. task post-approval drift). */
  afterProgress?: ReactNode;
};

export const STICKY_CHROME =
  'tw:sticky tw:top-0 tw:z-30 tw:w-full tw:border-b tw:border-[var(--border-primary)] tw:bg-black/95 tw:p-3 tw:backdrop-blur-sm';

export const BANNER_EMERALD =
  'tw:rounded-md tw:border tw:border-emerald-500 tw:bg-emerald-500/10 tw:px-3 tw:py-2 tw:text-xs tw:text-emerald-200';

export const BANNER_AMBER =
  'tw:rounded-md tw:border tw:border-amber-500 tw:bg-amber-500/10 tw:px-3 tw:py-2 tw:text-xs tw:text-amber-200';

const BANNER_SKY =
  'tw:rounded-md tw:border tw:border-sky-500 tw:bg-sky-500/10 tw:px-3 tw:py-2 tw:text-xs tw:text-sky-200';

const formatLastUpdated = (
  updatedAt?: string | null,
  updatedByUserName?: string | null
) => {
  if (!updatedAt) return null;
  const when = new Date(updatedAt);
  if (Number.isNaN(when.getTime())) return null;
  return `Last updated at ${when.toLocaleString()} by ${
    updatedByUserName?.trim() || 'Unknown'
  }`;
};

const ActionMenus = ({
  createItems,
  historyCount,
  signedCount,
  onOpenHistory,
  onOpenSigned,
  onDraftNewVersion,
  draftNewVersionDisabled = false,
  signedMenuLabel,
  hideSignedMenu = false,
}: {
  createItems: CreateMenuItem[];
  historyCount: number;
  signedCount: number;
  onOpenHistory: () => void;
  onOpenSigned: () => void;
  onDraftNewVersion?: () => void;
  draftNewVersionDisabled?: boolean;
  signedMenuLabel: string;
  hideSignedMenu?: boolean;
}) => {
  const createTour = createItems.find((item) => item.tour)?.tour;
  return (
    <div
      className="tw:ml-auto tw:flex tw:shrink-0 tw:flex-wrap tw:items-center tw:gap-2"
      data-testid="ics204-version-status-actions"
    >
      {createItems.length > 0 ? (
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="tw:h-7 tw:shrink-0 tw:gap-1 tw:text-xs"
                data-testid="ics204-create-version-menu"
                data-tour={createTour}
              >
                <Plus className="tw:h-3.5 tw:w-3.5" />
                Create
                <ChevronDown className="tw:h-3 tw:w-3 tw:opacity-60" />
              </Button>
            }
          />
          <DropdownMenuContent align="end" className="dark tw:text-xs">
            {createItems.map((item) => (
              <DropdownMenuItem
                key={item.key}
                disabled={item.disabled}
                data-testid={`ics204-create-${item.key}`}
                data-tour={item.tour}
                onClick={item.onSelect}
              >
                {item.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="tw:h-7 tw:shrink-0 tw:gap-1 tw:text-xs"
              data-testid="ics204-versions-menu"
            >
              <History className="tw:h-3.5 tw:w-3.5" />
              Versions
              <Badge
                variant="secondary"
                className="tw:ml-1 tw:h-4 tw:px-1.5 tw:text-[10px]"
              >
                {historyCount}
              </Badge>
              <ChevronDown className="tw:h-3 tw:w-3 tw:opacity-60" />
            </Button>
          }
        />
        <DropdownMenuContent align="end" className="dark tw:w-52 tw:text-xs">
          {onDraftNewVersion ? (
            <DropdownMenuItem
              data-testid="ics204-draft-new-version"
              disabled={draftNewVersionDisabled}
              onClick={onDraftNewVersion}
            >
              + Draft New Version
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuItem
            data-testid="ics204-open-version-history"
            onClick={onOpenHistory}
          >
            <History className="tw:mr-2 tw:h-3.5 tw:w-3.5" />
            Version history
            <Badge
              variant="secondary"
              className="tw:ml-auto tw:h-4 tw:px-1.5 tw:text-[10px]"
            >
              {historyCount}
            </Badge>
          </DropdownMenuItem>
          {hideSignedMenu ? null : (
            <DropdownMenuItem
              data-testid="ics204-open-signed-versions"
              disabled={signedCount === 0}
              onClick={onOpenSigned}
            >
              <Check className="tw:mr-2 tw:h-3.5 tw:w-3.5" />
              {signedMenuLabel}
              <Badge
                variant="secondary"
                className="tw:ml-auto tw:h-4 tw:px-1.5 tw:text-[10px]"
              >
                {signedCount}
              </Badge>
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

const Ics204VersionStatusBar = ({
  signatures,
  viewingPast,
  isCreatingSignedVersion,
  historyCount,
  signedCount,
  createItems,
  updatedAt,
  updatedByUserName,
  onOpenHistory,
  onOpenSigned,
  onViewLatest,
  onCancelCreateSigned,
  onSignThisVersion,
  onDraftNewVersion,
  draftNewVersionDisabled = false,
  getCompletion = getSignatureCompletion,
  signedMenuLabel = 'Signed versions',
  hideProgressRing = false,
  lockedMessage = 'Signed versions are locked from edits.',
  hideLockedMessage = false,
  progressTooltipTitle,
  embedded = false,
  hideSignedMenu = false,
  trailing,
  afterProgress,
}: Ics204VersionStatusBarProps) => {
  const completion = getCompletion(signatures);
  const locked = completion.status !== 'draft' && !isCreatingSignedVersion;
  const lastUpdated = formatLastUpdated(updatedAt, updatedByUserName);

  if (isCreatingSignedVersion) {
    return (
      <div
        className={embedded ? undefined : STICKY_CHROME}
        data-testid="ics204-version-status-bar"
      >
        <div
          className={cn(
            BANNER_SKY,
            'tw:flex tw:flex-wrap tw:items-center tw:gap-x-3 tw:gap-y-2'
          )}
        >
          <div className="tw:inline-flex tw:min-w-0 tw:shrink tw:flex-wrap tw:items-center tw:gap-x-2 tw:gap-y-1">
            <span>
              Creating a signed version — only you can view and edit until you
              sign.
            </span>
            {lastUpdated ? (
              <span
                className="tw:opacity-90"
                data-testid="ics204-version-last-updated"
              >
                {lastUpdated}
              </span>
            ) : null}
          </div>
          <div className="tw:ml-auto tw:flex tw:shrink-0 tw:flex-wrap tw:items-center tw:gap-2">
            {onSignThisVersion ? (
              <Button
                type="button"
                size="sm"
                className="tw:h-7 tw:text-xs"
                data-testid="ics204-sign-this-version"
                onClick={onSignThisVersion}
              >
                Sign this version
              </Button>
            ) : null}
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="tw:h-7 tw:text-xs"
              data-testid="ics204-cancel-create-signed"
              onClick={onCancelCreateSigned}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (viewingPast) {
    return (
      <div
        className={embedded ? undefined : STICKY_CHROME}
        data-testid="ics204-version-status-bar"
      >
        <div
          className={cn(
            BANNER_AMBER,
            'tw:flex tw:flex-wrap tw:items-center tw:gap-x-3 tw:gap-y-2'
          )}
        >
          <div className="tw:inline-flex tw:min-w-0 tw:shrink tw:flex-wrap tw:items-center tw:gap-x-2 tw:gap-y-1">
            <span className="tw:inline-flex tw:flex-wrap tw:items-center tw:gap-1.5">
              <History className="tw:h-3.5 tw:w-3.5" />
              Viewing a historical version (read-only).
              {hideProgressRing ? null : (
                <Ics204SignatureProgressRing
                  completion={completion}
                  progressTooltipTitle={progressTooltipTitle}
                />
              )}
            </span>
            {lastUpdated ? (
              <span
                className="tw:opacity-90"
                data-testid="ics204-version-last-updated"
              >
                {lastUpdated}
              </span>
            ) : null}
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="tw:ml-auto tw:h-7 tw:shrink-0 tw:text-xs"
            data-testid="ics204-view-latest-version"
            onClick={onViewLatest}
          >
            View latest
          </Button>
          {trailing}
        </div>
      </div>
    );
  }

  return (
    <div
      className={embedded ? undefined : STICKY_CHROME}
      data-testid="ics204-version-status-bar"
    >
      <div
        className={cn(
          BANNER_EMERALD,
          'tw:flex tw:flex-wrap tw:items-center tw:gap-x-3 tw:gap-y-2'
        )}
      >
        <div className="tw:inline-flex tw:min-w-0 tw:shrink tw:flex-wrap tw:items-center tw:gap-x-2 tw:gap-y-1">
          {hideProgressRing ? null : (
            <Ics204SignatureProgressRing
              completion={completion}
              progressTooltipTitle={progressTooltipTitle}
            />
          )}
          {afterProgress}
          {lastUpdated ? (
            <span
              className="tw:opacity-90"
              data-testid="ics204-version-last-updated"
            >
              {lastUpdated}
            </span>
          ) : null}
          {locked && !hideLockedMessage ? (
            <span className="tw:inline-flex tw:items-center tw:gap-1 tw:opacity-90">
              <Lock className="tw:h-3.5 tw:w-3.5" />
              {lockedMessage}
            </span>
          ) : null}
        </div>
        <ActionMenus
          createItems={createItems}
          historyCount={historyCount}
          signedCount={signedCount}
          onOpenHistory={onOpenHistory}
          onOpenSigned={onOpenSigned}
          onDraftNewVersion={onDraftNewVersion}
          draftNewVersionDisabled={draftNewVersionDisabled}
          signedMenuLabel={signedMenuLabel}
          hideSignedMenu={hideSignedMenu}
        />
        {trailing}
      </div>
    </div>
  );
};

export default Ics204VersionStatusBar;
